/** Published evidence, reviewed 11 October 2026. Never interpolate a resort forecast from these global ranges. */
export const REVIEWED_ON = "2026-10-11";
export const SOURCES = {
  temperature: {
    label: "IPCC AR6 · sea temperature",
    year: 2021,
    url: "https://www.ipcc.ch/report/ar6/wg1/chapter/chapter-9/",
    note: "Section 9.2.1.1 and Figure 9.3. Global mean sea-surface temperature change in 2081–2100 versus 1995–2014; CMIP6 means and 5–95% model ranges from the chapter's detailed assessment. This is neither absolute water temperature nor a beach-level forecast.",
  },
  warming: {
    label: "IPCC AR6 · warming",
    year: 2021,
    url: "https://www.ipcc.ch/report/ar6/wg1/chapter/summary-for-policymakers/",
    note: "Table SPM.1. Global surface temperature relative to 1850–1900; best estimates and very likely ranges. SSPs are conditional pathways, not probability-ranked forecasts.",
  },
  sea: {
    label: "IPCC AR6 · sea level",
    year: 2021,
    url: "https://www.ipcc.ch/report/ar6/wg1/chapter/chapter-9/",
    note: "Final Table 9.9, medium-confidence processes. Global mean sea-level rise relative to 1995–2014; medians and likely ranges. Local land movement, ocean circulation and low-likelihood ice-sheet outcomes are not represented.",
  },
  heat: {
    label: "IPCC AR6 · marine heatwaves",
    year: 2021,
    url: "https://www.ipcc.ch/report/ar6/wg1/chapter/chapter-12/",
    note: "Section 12.4.7 and Box 9.2: global frequency in 2081–2100 relative to 1995–2014. Reported model ranges, not a prediction for a particular beach.",
  },
  ski: {
    label: "François et al. · European skiing",
    year: 2023,
    url: "https://www.nature.com/articles/s41558-023-01759-5",
    note: "2,234 resorts across 28 European countries at +2°C and +4°C global warming. Very high snow-supply risk is a study-defined hazard, not a probability of resort closure. Adaptation assumes snowmaking on 50% of slopes.",
  },
  regions: {
    label: "INRAE · regional ski results",
    year: 2023,
    url: "https://www.inrae.fr/en/actualites/Ski_Europe",
    note: "Research team's regional explanation of François et al. Percentages at +2°C; the French Alps value without snowmaking is approximately one third.",
  },
  elevation: {
    label: "Spandre et al. · snow and elevation",
    year: 2019,
    url: "https://tc.copernicus.org/articles/13/1325/2019/",
    note: "Section 3.1.2. Regional median elevations during 2030–2050 across RCP2.6, 4.5 and 8.5. Groomed natural snow: at least 100 days with 100 kg/m² between 15 December and 15 April in 7 winters out of 10. These are scenario spreads, not uncertainty percentiles or universal snowlines.",
  },
  mountains: {
    label: "IPCC · mountain futures",
    year: 2022,
    url: "https://www.ipcc.ch/report/ar6/wg2/chapter/ccp5/",
    note: "Cross-Chapter Paper 5. Mountain snow, glacier and permafrost impacts, with differences by elevation and latitude.",
  },
  international: {
    label: "IPCC · international ski outlook",
    year: 2019,
    url: "https://www.ipcc.ch/srocc/chapter/chapter-2/",
    note: "High mountain assessment. Regional ski-season risks depend on emissions, altitude, latitude and adaptation. High-emissions late-century outcomes are not near-term forecasts.",
  },
  rockies: {
    label: "Adhikari et al. · Rocky Mountains",
    year: 2026,
    url: "https://www.nature.com/articles/s41612-026-01530-5",
    note: "Peer-reviewed early publication, 3 September 2026. Regional simulations find greater relative resilience at high, snowy resorts; Colorado resorts generally lose less snow security than resorts nearer the Pacific coast.",
  },
  waves: {
    label: "Morim et al. · wave climate",
    year: 2019,
    url: "https://www.nature.com/articles/s41558-019-0542-5",
    note: "Multi-model wave projections under RCP8.5, 2081–2100 versus 1979–2004. Regional mean height and period changes of 5–15%, direction changes of 5–15°. These are not changes in surf quality at individual breaks.",
  },
  waveRegions: {
    label: "IPCC · regional waves",
    year: 2019,
    url: "https://www.ipcc.ch/srocc/chapter/summary-for-policymakers/",
    note: "Projected significant wave-height increases in the Southern Ocean and tropical eastern Pacific, and decreases in the North Atlantic and Mediterranean under RCP8.5. Mean changes do not describe every storm or season.",
  },
  coral: {
    label: "IPCC · coral reefs",
    year: 2018,
    url: "https://www.ipcc.ch/sr15/chapter/spm/",
    note: "Further warm-water coral reef decline of 70–90% at 1.5°C (high confidence) and more than 99% at 2°C (very high confidence), compared with the report's present-day baseline. Not a measurement of reef loss since 2026.",
  },
  permafrost: {
    label: "NSIDC · permafrost feedback",
    year: 2026,
    url: "https://nsidc.org/learn/parts-cryosphere/frozen-ground-permafrost/why-frozen-ground-matters",
    note: "Process explanation: thawed organic matter can release carbon dioxide and methane. Page accessed October 2026; access year is not a new projection publication.",
  },
  oceanLife: {
    label: "IPCC · ocean ecosystems",
    year: 2022,
    url: "https://www.ipcc.ch/report/ar6/wg2/chapter/chapter-3/",
    note: "Ocean warming, acidification, habitat loss and changing marine species distributions. Fish stocks also depend on fishing pressure and management.",
  },
  circulation: {
    label: "Met Office · Atlantic circulation",
    year: 2025,
    url: "https://www.metoffice.gov.uk/about-us/news-and-media/media-centre/weather-and-climate-news/2025/climate-change-amoc-likely-to-withstand-future-warming",
    note: "The Atlantic overturning circulation is expected to weaken. A dated collapse forecast or a claim that the jet stream simply breaks down would overstate the evidence.",
  },
  policy: {
    label: "UNEP · Emissions Gap Report",
    year: 2025,
    url: "https://www.unep.org/resources/emissions-gap-report-2025",
    note: "Current-policy warming around 2.8°C over this century; full national pledges 2.3–2.5°C. Annual greenhouse-gas emissions in 2035 need to be 35% / 55% below 2019 for 2°C / 1.5°C-aligned pathways. This policy assessment is separate from the IPCC SSP experiments.",
  },
  tides: {
    label: "NOAA · higher sea levels and tides",
    year: 2026,
    url: "https://oceanservice.noaa.gov/facts/high-tide-flooding.html",
    note: "Higher background sea level increases high-tide flooding. It does not imply that every location's astronomical tidal range increases. Accessed October 2026.",
  },
} as const;
export type SourceKey = keyof typeof SOURCES;
export type Projection = { value: number; low: number; high: number };
export type PathwayKey = "low" | "middle" | "high";
export const PATHWAYS: {
  key: PathwayKey;
  label: string;
  code: string;
  colour: string;
  description: string;
}[] = [
  {
    key: "low",
    label: "Lower emissions",
    code: "SSP1-2.6",
    colour: "#5BC0CE",
    description: "Strong, sustained emissions reductions.",
  },
  {
    key: "middle",
    label: "Intermediate",
    code: "SSP2-4.5",
    colour: "#F0BE77",
    description: "An intermediate emissions pathway.",
  },
  {
    key: "high",
    label: "Higher emissions",
    code: "SSP3-7.0",
    colour: "#EE927D",
    description: "Emissions continue rising for much of the century.",
  },
];
export const WARMING: Record<
  PathwayKey,
  Record<"2050" | "2090", Projection>
> = {
  low: {
    "2050": { value: 1.7, low: 1.3, high: 2.2 },
    "2090": { value: 1.8, low: 1.3, high: 2.4 },
  },
  middle: {
    "2050": { value: 2, low: 1.6, high: 2.5 },
    "2090": { value: 2.7, low: 2.1, high: 3.5 },
  },
  high: {
    "2050": { value: 2.1, low: 1.7, high: 2.6 },
    "2090": { value: 3.6, low: 2.8, high: 4.6 },
  },
};
export const SEA_LEVEL: Record<
  PathwayKey,
  Record<"2050" | "2100", Projection>
> = {
  low: {
    "2050": { value: 19, low: 16, high: 25 },
    "2100": { value: 44, low: 32, high: 62 },
  },
  middle: {
    "2050": { value: 20, low: 17, high: 26 },
    "2100": { value: 56, low: 44, high: 76 },
  },
  high: {
    "2050": { value: 22, low: 18, high: 27 },
    "2100": { value: 68, low: 55, high: 90 },
  },
};
export const SKI_RISK = [
  { name: "Europe · +2°C", warming: 2, natural: 53, snowmaking: 27 },
  { name: "Europe · +4°C", warming: 4, natural: 98, snowmaking: 71 },
];
export const REGIONAL_SKI_RISK = [
  { name: "French Alps", natural: 33, snowmaking: 7 },
  { name: "Pyrenees", natural: 89, snowmaking: 9 },
  { name: "French / Swiss mid-mountains", natural: 80, snowmaking: 56 },
];
export const ELEVATIONS = [
  { name: "Northern French Alps", low: 1850, high: 2000 },
  { name: "Southern French Alps", low: 2500, high: 2650 },
  { name: "French Pyrenees", low: 2250, high: 2300 },
  { name: "Spanish / Andorran Pyrenees", low: 2550, high: 2650 },
];
export const MARINE_HEATWAVES = [
  { name: "Lower · SSP1-2.6", low: 2, high: 9 },
  { name: "Very high · SSP5-8.5", low: 4, high: 18 },
];
export const SEA_TEMPERATURE = [
  { name: "SSP1-2.6", value: 0.86, low: 0.43, high: 1.47 },
  { name: "SSP2-4.5", value: 1.51, low: 1.02, high: 2.19 },
  { name: "SSP3-7.0", value: 2.19, low: 1.56, high: 3.3 },
];
export const CLIMATE_LOCATIONS = [
  {
    slug: "morzine",
    name: "Morzine, France",
    sport: "piste",
    region: "Northern French Alps",
    outlook:
      "Lower return runs face greater pressure than higher terrain. The regional study places the 2030–2050 groomed natural-snow reliability elevation at 1,850–2,000 m; individual slopes depend on aspect, weather and management.",
    source: "elevation",
  },
  {
    slug: "tignes",
    name: "Tignes, France",
    sport: "piste",
    region: "Northern French Alps",
    outlook:
      "Higher terrain offers a snow advantage within the northern Alps. Glacier loss and thawing mountain permafrost still affect access and infrastructure. Altitude does not guarantee a full season.",
    source: "mountains",
  },
  {
    slug: "hossegor",
    name: "Hossegor, France",
    sport: "surf",
    region: "North Atlantic",
    outlook:
      "North Atlantic mean wave heights are projected to decrease under high emissions. Sandbanks, swell direction, local wind and sea-level rise determine what that means at this beach; offshore averages cannot predict surf quality.",
    source: "waveRegions",
  },
  {
    slug: "newquay-fistral",
    name: "Newquay / Fistral, UK",
    sport: "surf",
    region: "North Atlantic",
    outlook:
      "Higher sea level changes the water depth over sandbanks and increases coastal flooding pressure. A projected fall in North Atlantic mean wave height does not rule out damaging storms or good surf days.",
    source: "waveRegions",
  },
] as const;
export type ClimateLocation = (typeof CLIMATE_LOCATIONS)[number];
export function climateLocation(
  slug: string | null,
): ClimateLocation | undefined {
  return CLIMATE_LOCATIONS.find((place) => place.slug === slug);
}
